type SimpleAttr = string | number | boolean;
type StyleAttr = Partial<CSSStyleDeclaration> | Record<string, string>;
type TrustedHtml = {
  readonly markup: string;
  readonly trustedHtml: true;
};
type EventAttr = (event: Event) => void;
type ElAttrValue = SimpleAttr | TrustedHtml | EventAttr | StyleAttr | null | undefined;
type ElAttrs = Record<string, ElAttrValue>;
type ElChild = Node | string | null | false | undefined;

const SVG_NS = "http://www.w3.org/2000/svg";

const simpleAttrSetters: Record<string, (el: Element, value: string) => void> = {
  class: (el, value) => el.setAttribute("class", value),
  text: (el, value) => {
    el.textContent = value;
  },
};

export const trustedHtml = (markup: string): TrustedHtml => ({
  markup,
  trustedHtml: true,
});

const isSimple = (value: ElAttrValue): value is SimpleAttr =>
  typeof value === "string" || typeof value === "number" || typeof value === "boolean";

const isTrustedHtml = (value: ElAttrValue): value is TrustedHtml =>
  typeof value === "object" && value != null && "trustedHtml" in value;

const setSimpleAttr = (el: Element, key: string, value: SimpleAttr): void => {
  const str = String(value);
  (simpleAttrSetters[key] ?? ((node, attrValue) => node.setAttribute(key, attrValue)))(el, str);
};

const setStyle = (el: Element, value: ElAttrValue): boolean => {
  if (typeof value !== "object" || value === null) return false;
  const style = (el as HTMLElement).style;
  Object.entries(value as Record<string, string>).forEach(([key, val]) => {
    if (key.startsWith("--")) style.setProperty(key, val);
    else Object.assign(style, { [key]: val });
  });
  return true;
};

const setHtml = (el: Element, value: ElAttrValue): boolean =>
  isTrustedHtml(value)
    ? (el.replaceChildren(document.createRange().createContextualFragment(value.markup)), true)
    : false;

const setListener = (el: Element, key: string, value: ElAttrValue): boolean =>
  key.startsWith("on")
    ? (typeof value === "function" && el.addEventListener(key.slice(2).toLowerCase(), value), true)
    : false;

const setAttr = (el: Element, key: string, value: ElAttrValue): void => {
  if (value === false || value == null) return;
  const handled =
    (key === "html" && setHtml(el, value)) ||
    (key === "style" && setStyle(el, value)) ||
    setListener(el, key, value);
  if (!handled && isSimple(value)) {
    setSimpleAttr(el, key, value);
  }
};

const appendChildren = (el: Element, list: ElChild[]): void =>
  list
    .filter((child): child is Node | string => child != null && child !== false)
    .forEach((child) =>
      el.appendChild(typeof child === "string" ? document.createTextNode(child) : child),
    );

export function h<K extends keyof HTMLElementTagNameMap>(
  tag: K,
  attrs?: ElAttrs,
  children?: ElChild | ElChild[],
): HTMLElementTagNameMap[K];
export function h(tag: string, attrs?: ElAttrs, children?: ElChild | ElChild[]): Element;
export function h(tag: string, attrs?: ElAttrs, children?: ElChild | ElChild[]): Element {
  const isSvg = tag.includes(":");
  const el = isSvg
    ? document.createElementNS(SVG_NS, tag.slice(tag.indexOf(":") + 1))
    : document.createElement(tag);

  if (attrs) {
    Object.entries(attrs).forEach(([key, val]) => setAttr(el, key, val));
  }

  if (children !== undefined && children !== null) {
    appendChildren(el, Array.isArray(children) ? children : [children]);
  }

  return el;
}

export const svg = (tag: string, attrs?: ElAttrs, children?: ElChild | ElChild[]): SVGElement =>
  h("svg:" + tag, attrs, children) as SVGElement;
